// ---------------------------------------------------------------------------
// On-device speech-to-text for the desktop build.
//
// The Rust backend statically links whisper.cpp (via whisper-rs) and the app
// ships a default English model (ggml-base.en-q5_1) as a bundled resource, so
// transcription works offline with zero setup.
//
// Whisper wants 16 kHz mono PCM. The webview can decode the MediaRecorder
// webm/opus blob natively (Web Audio), so decoding + resampling happens HERE
// and Rust receives raw PCM (i16 little-endian, base64) — the backend never
// needs an audio-container decoder.
// ---------------------------------------------------------------------------

import { invoke, isTauri } from '../ai/tauri';
import type { RecordedAudio } from '../hooks/useSpeech';

const WHISPER_SAMPLE_RATE = 16000;

export interface WhisperStatus {
  ok: boolean;
  detail: string;
}

let statusPromise: Promise<WhisperStatus> | null = null;

/**
 * Is on-device transcription available? Also warms the model up: the Rust side
 * loads it on first call and keeps it for the process lifetime, so the first
 * real transcription doesn't pay the ~1 s load. Cached — the bundled model
 * can't change at runtime. Only an IPC-level failure clears the cache so a
 * later call can retry.
 */
export function whisperStatus(): Promise<WhisperStatus> {
  if (!isTauri()) {
    return Promise.resolve({
      ok: false,
      detail: 'On-device transcription only exists in the desktop build.',
    });
  }
  statusPromise ??= invoke<WhisperStatus>('whisper_status').catch((e) => {
    statusPromise = null;
    return { ok: false, detail: `Whisper is unavailable: ${String(e)}` };
  });
  return statusPromise;
}

/** Decode any browser-playable audio bytes to 16 kHz mono i16 PCM. */
async function decodeTo16kMonoPcm(bytes: Uint8Array): Promise<Int16Array> {
  // decodeAudioData detaches the ArrayBuffer it's given — hand it a copy so
  // the caller's RecordedAudio stays usable (e.g. for playback).
  const copy = bytes.slice().buffer;
  const probe = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await probe.decodeAudioData(copy);
  } finally {
    void probe.close();
  }

  // Resample + downmix to mono in one pass with an OfflineAudioContext.
  const frames = Math.max(1, Math.ceil(decoded.duration * WHISPER_SAMPLE_RATE));
  const offline = new OfflineAudioContext(1, frames, WHISPER_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();

  const f32 = rendered.getChannelData(0);
  const i16 = new Int16Array(f32.length);
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]));
    i16[i] = Math.round(s < 0 ? s * 0x8000 : s * 0x7fff);
  }
  return i16;
}

function i16ToB64(samples: Int16Array): string {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
  let binary = '';
  const chunk = 0x8000; // avoid arg-count limits in String.fromCharCode
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Transcribe a recorded answer with the bundled Whisper model.
 * Throws with a readable message on failure (too-short recording, etc.).
 */
export async function transcribeRecording(audio: RecordedAudio): Promise<string> {
  const pcm = await decodeTo16kMonoPcm(audio.bytes);
  return invoke<string>('whisper_transcribe', { pcmB64: i16ToB64(pcm) });
}
